import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import AddToBasketDialog from 'components/AddToBasket/AddToBasketDialog/AddToBasketDialog';
import DigitalDownloadDialog from 'components/DigitalDownloadDialog/DigitalDownloadDialog';
import { useSelectedSkuContext } from 'context/SelectedSku/SelectedSkuContext';
import { CheckItsRightDialog } from 'components/Insurance/InsuranceDialog/CheckItsRightDialog';
import { InsuranceOffer } from 'types/Product';
import { gridConfig } from '@tvg/fuse-design';

const DialogServiceContainer = styled.div`
  position: absolute;
`;

const AddToBasketRecsContainer = styled.div<{ hasATBRecsCarousel: boolean }>`
  margin: var(--spacing-space24) auto 0;
  max-width: ${({ hasATBRecsCarousel }) => (hasATBRecsCarousel ? '364px' : '200px')};

  @media (min-width: ${gridConfig.l.minWidth}) {
    max-width: ${({ hasATBRecsCarousel }) => (hasATBRecsCarousel ? '440px' : '200px')};
  }

  // This element will be replaced when recommendations are loaded.
  // Initially hiding it prevents a bug where a skeleton component is rendered.
  [data-stateid='recs2_atb_1'] {
    display: none;
  }
`;

const addToBasketRecsContainerId = 'recs-atb-container';
const recsEsi = '<esi:include src="/fragment/recommendations-fe/recommendations/recs2/atb_1" />';

export function DialogService() {
  const {
    selectedSku,
    setSelectedInsurance,
    showCheckItsRightDialog,
    setCheckItsRightDialog,
    isAddToBasketDialogOpen,
    closeBasketDialog,
    handleContinueShopping,
    handleGoToBasket,
    recsDialogRef,
    recsAtbRef,
    isAddToBasketDialogRendered,
    isDigitalDownloadDialogRendered,
    handleDigitalDownloadAddToBasket,
    setIsDigitalDownloadDialogOpen,
    isDigitalDownloadDialogOpen,
    selectedInsurance,
    isCheckItsRightDialogRendered,
    selectedSkuInsuranceContent,
    showFindOutMore,
    setFindOutMore,
  } = useSelectedSkuContext();

  const [hasATBRecsCarousel, setHasATBRecsCarousel] = useState(false);

  const insuranceTitle = selectedSku?.insuranceOffers && selectedSku?.insuranceOffers[0]?.offer?.productDescription;
  const handleInsuranceOptionSelect = (option: InsuranceOffer | string | null) => {
    setSelectedInsurance(option);
    if (!showCheckItsRightDialog) {
      setCheckItsRightDialog(true);
    }
  };

  const handleNoInsuranceSelect = useCallback(() => {
    setTimeout(() => setSelectedInsurance('No insurance'), 200);
  }, []);

  const handleContinueWithoutInsurance = () => {
    handleNoInsuranceSelect();
    if (showFindOutMore) {
      setFindOutMore(false);
    }
    setCheckItsRightDialog(false);
  };

  const handleCheckItsRightClose = () => {
    setTimeout(() => {
      if (!showFindOutMore) {
        setSelectedInsurance(null);
      }
      setFindOutMore(false);
    }, 200);
    setCheckItsRightDialog(false);
  };

  const handleAddInsuranceButton = () => {
    if (showFindOutMore) {
      setFindOutMore(false);
    }
    setCheckItsRightDialog(false);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const atbRecsCarousel = document.querySelector('[data-testid="recommendations-carousel-add-to-basket"]');

      if (atbRecsCarousel) {
        setHasATBRecsCarousel(true);
        clearInterval(interval);
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 15000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (
      isAddToBasketDialogOpen &&
      recsAtbRef &&
      'current' in recsAtbRef &&
      recsAtbRef.current &&
      recsDialogRef &&
      'current' in recsDialogRef &&
      recsDialogRef.current
    ) {
      recsDialogRef.current.appendChild(recsAtbRef.current);
      recsAtbRef.current.removeAttribute('hidden');
    }
  }, [isAddToBasketDialogOpen]);

  return (
    <>
      {selectedSku ? (
        <DialogServiceContainer>
          {isCheckItsRightDialogRendered && insuranceTitle && selectedSkuInsuranceContent && (
            <CheckItsRightDialog
              title={insuranceTitle}
              options={selectedSku.insuranceOffers}
              onClose={handleCheckItsRightClose}
              selectedInsurance={selectedInsurance}
              open={showCheckItsRightDialog}
              handleSelectedInsurance={handleInsuranceOptionSelect}
              selectedSkuInsuranceContent={selectedSkuInsuranceContent}
              findOutMoreView={showFindOutMore}
              handleContinueWithoutInsurance={handleContinueWithoutInsurance}
              onAddInsuranceClick={handleAddInsuranceButton}
              selectedSku={selectedSku}
            />
          )}
          {isDigitalDownloadDialogRendered && (
            <DigitalDownloadDialog
              onClick={handleDigitalDownloadAddToBasket}
              onClose={() => setIsDigitalDownloadDialogOpen(false)}
              open={isDigitalDownloadDialogOpen}
            />
          )}
          {isAddToBasketDialogRendered && (
            <AddToBasketDialog
              open={isAddToBasketDialogOpen}
              onClose={closeBasketDialog}
              handleContinueShopping={handleContinueShopping}
              handleGoToBasket={handleGoToBasket}
              ref={recsDialogRef}
            />
          )}
        </DialogServiceContainer>
      ) : null}
      <div>
        <AddToBasketRecsContainer
          ref={recsAtbRef}
          hasATBRecsCarousel={hasATBRecsCarousel}
          id={addToBasketRecsContainerId}
          data-testid="recs-atb"
          hidden
          dangerouslySetInnerHTML={{
            __html: recsEsi,
          }}
        />
      </div>
    </>
  );
}

export default DialogService;
